import type { Ref } from 'vue'

import { handleFileUpload, poll } from '@backmarket/front-upload'

import { getHttpBaseUrl } from '@backmarket/nuxt-module-http/getHttpBaseUrl'

import type { ErrorCause, ErrorCode } from '../types/UploadFile'

export * from '../types/UploadFile'

export class UploadFileError<Code extends ErrorCode> extends Error {
  constructor(
    public code: Code,
    public override cause?: ErrorCause<Code>,
  ) {
    super(`Upload file error: ${code}`)
  }
}

/**
 * Upload a file through our "upload service". It relies on the `front-upload`
 * library under the hood, so check the documentation there to know more.
 *
 * @see https://github.com/BackMarket/front-upload
 */
export async function uploadFile(
  rule: Parameters<typeof handleFileUpload>[1],
  context: Parameters<typeof handleFileUpload>[2],
  upload: Parameters<typeof handleFileUpload>[3],
  cancelPoll: Ref<(() => void) | undefined>,
): ReturnType<typeof handleFileUpload> {
  return new Promise((resolve, reject) => {
    // Promise executors (callback passed as parameter) can't be async. However,
    // we still want to use `async/await` in here as it simplifies the code
    // and makes the whole think easier to understand. So we create an inner
    // function that we mark as `async` (that, we're allowed), and fire it.
    async function innerUploadFile() {
      const uploadServiceUrl = `${getHttpBaseUrl()}/services/uploads`

      try {
        const uploadedFile = await handleFileUpload(
          uploadServiceUrl,
          rule,
          context,
          upload,
        )

        if (uploadedFile.status === 'failed') {
          reject(
            new UploadFileError('failed', {
              error: uploadedFile.errors,
              handle: uploadedFile.handle,
            }),
          )

          // If the upload failed right away, there is no need start polling,
          // so we reject the promise and stop the function execution here.
          return
        }

        const cancelFn = await poll(uploadServiceUrl, uploadedFile.handle, {
          onSuccess(payload) {
            resolve({ ...uploadedFile, ...payload })
          },
          onError(error) {
            reject(new UploadFileError('error', { error }))
          },
          onFailure(payload) {
            reject(
              new UploadFileError('failure', {
                error: payload.statusReason,
                handle: payload.handle,
              }),
            )
          },
          onTimeout() {
            reject(new UploadFileError('timeout'))
          },
        })

        // We expose the cancel function through the given ref.
        // eslint-disable-next-line no-param-reassign
        cancelPoll.value = cancelFn
      } catch (error) {
        reject(
          new UploadFileError('error', {
            error: typeof error === 'string' ? error : undefined,
          }),
        )
      }
    }

    // We don't care of the returned value here. We rely on this inner function
    // as a way to use the `async/await` syntax freely, as explained above.
    void innerUploadFile()
  })
}
